import React, { useState, useEffect, useCallback } from "react";
import { Spinner } from "../../Components/Common/LoadingStates";
import { getRewardsVault, refillRewardsVault } from "../../helpers/fakebackend_helper";
import { TOKEN, card, btnGhost, input, busyBtn, fmtNum, Banner, Stat, StatGrid, ReadOnlyNote, label } from "./shared";
import { apiError } from "./rewardsUtil";

export default function RewardsWalletTab({ flash, canRefill }) {
  const [vault, setVault] = useState(null);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastRefill, setLastRefill] = useState(null);

  const load = useCallback(async () => {
    try { setVault(await getRewardsVault()); }
    catch (err) { flash("error", apiError(err, "Failed to load the rewards wallet")); setVault({}); }
  }, [flash]);
  useEffect(() => { load(); }, [load]);

  const refill = async (e) => {
    e.preventDefault();
    const n = Number(amount);
    if (!Number.isFinite(n) || n <= 0) { flash("error", "Enter a positive amount"); return; }
    setBusy(true);
    try {
      const r = await refillRewardsVault(n);
      const funded = Number(r?.funded_claims || 0);
      setLastRefill({ amount: n, balance: r?.balance_byb, funded });
      flash(
        "success",
        `Rewards wallet balance: ${fmtNum(r?.balance_byb)} ${TOKEN}. ${funded === 1 ? "1 waiting claim was" : `${fmtNum(funded)} waiting claims were`} funded.`
      );
      setAmount("");
      await load();
    } catch (err) { flash("error", `Refill failed: ${apiError(err, "unknown error")}`); }
    finally { setBusy(false); }
  };

  if (vault === null) return <div style={card}><Spinner /></div>;
  const hasOnchain = vault.onchain_treasury_balance !== null && vault.onchain_treasury_balance !== undefined;

  return (
    <div>
      {vault.low_balance && (
        <Banner tone="warn">
          The rewards wallet is running low ({fmtNum(vault.balance_byb)} {TOKEN}). New claims will wait for funding until it is refilled.
        </Banner>
      )}
      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h5 style={{ margin: 0 }}>Rewards wallet budget</h5>
          <button style={btnGhost} onClick={load}>Refresh</button>
        </div>
        <StatGrid>
          <Stat label={`Balance (${TOKEN})`} value={fmtNum(vault.balance_byb)} />
          <Stat label="Reserved for funded claims" value={fmtNum(vault.reserved)} />
          <Stat label="Awaiting funding (points)" value={fmtNum(vault.awaiting_funding_points)} />
          {hasOnchain && <Stat label={`On-chain treasury (${TOKEN})`} value={fmtNum(vault.onchain_treasury_balance)} />}
        </StatGrid>

        {canRefill ? (
          <form onSubmit={refill} style={{ display: "flex", gap: 8, maxWidth: 420, marginTop: 16 }}>
            <input style={input} type="number" min="0" step="any" placeholder={`Amount to add (${TOKEN})`} value={amount} onChange={(e) => setAmount(e.target.value)} />
            <button style={busyBtn(busy)} disabled={busy}>Refill</button>
          </form>
        ) : (
          <ReadOnlyNote>Read only. Refilling needs the rewards.payouts permission.</ReadOnlyNote>
        )}
        {lastRefill && (
          <div style={{ ...label, marginTop: 10 }}>
            Last refill: +{fmtNum(lastRefill.amount)} {TOKEN}, balance now {fmtNum(lastRefill.balance)} {TOKEN}, {fmtNum(lastRefill.funded)} waiting {lastRefill.funded === 1 ? "claim" : "claims"} funded.
          </div>
        )}
        <p style={{ ...label, marginTop: 10 }}>
          {hasOnchain
            ? `This is the off-chain payout budget. Refilling funds waiting claims oldest first while the balance covers them. Compare it with the on-chain treasury balance above before refilling.`
            : `This is the off-chain payout budget. Keep it in step with the treasury wallet's real ${TOKEN} balance by hand. Refilling funds waiting claims oldest first while the balance covers them.`}
        </p>
      </div>
    </div>
  );
}
